<template>
  <div class="application-page" :style="{ backgroundImage: `url(${backgroundImage})` }">
    <img class="application-top-banner" :src="topBanner" alt="">
    <header class="application-header">
      <div class="application-meta">当前用户：{{ userLabel }}</div>
    </header>

    <main class="application-main">
      <div class="application-page-actions">
        <button
          type="button"
          class="application-tab"
          :class="{ active: activeView === 'apply' }"
          @click="setActiveView('apply')"
        >申请</button>
        <button
          type="button"
          class="application-tab"
          :class="{ active: activeView === 'approval' }"
          @click="setActiveView('approval')"
        >
          流程审批<span v-if="approvalTotal" class="application-tab-badge">{{ approvalTotal }}</span>
        </button>
        <button type="button" class="header-action" @click="goDashboard">返回大屏</button>
        <button type="button" class="header-action" @click="handleLogout">退出</button>
      </div>

      <section v-if="activeView === 'approval'" class="workspace-panel" aria-labelledby="approval-title">
          <div class="workspace-toolbar">
            <div>
              <h2 id="approval-title">待处理事项</h2>
              <span class="toolbar-caption">当前需要处理的申请</span>
            </div>
            <div class="toolbar-actions">
              <label class="toolbar-field">
                <span>科室</span>
                <input v-model.trim="approvalQuery.department" type="text" placeholder="请输入科室" @keyup.enter="searchApproval">
              </label>
              <label class="toolbar-field">
                <span>姓名</span>
                <input v-model.trim="approvalQuery.name" type="text" placeholder="请输入姓名" @keyup.enter="searchApproval">
              </label>
              <button type="button" class="outline-button" @click="searchApproval">搜索</button>
              <button type="button" class="icon-button" aria-label="刷新流程审批列表" title="刷新" @click="loadApproval">↻</button>
            </div>
          </div>

          <div v-if="approvalError" class="inline-error">
            <span>{{ approvalError }}</span>
            <button type="button" @click="loadApproval">重试</button>
          </div>
          <div v-else-if="loading.approval" class="table-state">正在加载审批事项...</div>
          <div v-else class="table-wrap">
            <table class="data-table">
              <thead>
                <tr>
                  <th>申请人</th>
                  <th>流程</th>
                  <th>科室 / 姓名</th>
                  <th>请假日期</th>
                  <th>申请时间</th>
                  <th>事由</th>
                  <th class="table-action-column">操作</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in approvalRows" :key="row.id">
                  <td class="strong-cell">{{ row.applicantName || '-' }}</td>
                  <td>{{ row.processName || '-' }}</td>
                  <td>{{ personLabel(row) }}</td>
                  <td>{{ dateRangeLabel(row) }}</td>
                  <td>{{ row.createTime || '-' }}</td>
                  <td class="reason-cell" :title="row.reason || ''">{{ row.reason || '-' }}</td>
                  <td class="table-action-column"><button type="button" class="text-button" @click="openApprovalDetail(row)">审核</button></td>
                </tr>
                <tr v-if="!approvalRows.length">
                  <td colspan="7" class="empty-cell">暂无待处理的申请</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="table-footer">
            <span>共 {{ approvalTotal }} 条</span>
            <div class="pagination-actions">
              <button type="button" class="page-button" :disabled="approvalQuery.pageNum <= 1" @click="changePage('approval', -1)">上一页</button>
              <span>第 {{ approvalQuery.pageNum }} / {{ approvalPageCount }} 页</span>
              <button type="button" class="page-button" :disabled="approvalQuery.pageNum >= approvalPageCount" @click="changePage('approval', 1)">下一页</button>
            </div>
          </div>
        </section>

        <section v-else class="apply-workspace" aria-labelledby="apply-title">
          <div v-if="processError" class="inline-error">
            <span>{{ processError }}</span>
            <button type="button" @click="loadProcesses">重试</button>
          </div>
          <div class="apply-grid">
            <section class="form-panel">
              <div class="panel-heading">
                <div>
                  <h2 id="apply-title">申请</h2>
                  <span>填写后提交，申请将进入对应审批流程</span>
                </div>
                <button v-if="form.id" type="button" class="small-link" @click="resetApplicationForm">新建</button>
              </div>
              <form class="application-form" @submit.prevent="submitApplication">
                <label class="form-field">
                  <span>请假流程 <em>*</em></span>
                  <el-select
                    v-model="form.processId"
                    class="application-control"
                    popper-class="application-select-popper"
                    :disabled="loading.processes"
                    filterable
                    placeholder="请选择请假流程"
                    @change="handleProcessChange"
                  >
                    <el-option
                      v-for="process in processes"
                      :key="process.id"
                      :label="process.processName"
                      :value="process.id"
                    />
                  </el-select>
                </label>

                <label v-if="form.processId" class="form-field">
                  <span>审批通过后状态 <em>*</em></span>
                  <el-select
                    v-model="form.leaveStatus"
                    class="application-control"
                    popper-class="application-select-popper"
                    :disabled="loading.processes"
                    placeholder="请选择审批通过后状态"
                  >
                    <el-option
                      v-for="status in leaveStatusOptions"
                      :key="status"
                      :label="status"
                      :value="status"
                    />
                  </el-select>
                </label>

                <div class="form-field-group">
                  <label class="form-field">
                    <span>开始日期 <em>*</em></span>
                    <el-date-picker
                      v-model="form.startDate"
                      class="application-control"
                      popper-class="application-date-popper"
                      type="date"
                      value-format="yyyy-MM-dd"
                      placeholder="请选择开始日期"
                      @change="loadDutyOptionsFromForm"
                    />
                  </label>
                  <label class="form-field">
                    <span>结束日期 <em>*</em></span>
                    <el-date-picker
                      v-model="form.endDate"
                      class="application-control"
                      popper-class="application-date-popper"
                      type="date"
                      value-format="yyyy-MM-dd"
                      placeholder="请选择结束日期"
                      @change="loadDutyOptionsFromForm"
                    />
                  </label>
                </div>

                <label class="form-field">
                  <span>科室 / 姓名 <em>*</em></span>
                  <el-select
                    v-model="form.personKey"
                    class="application-control"
                    popper-class="application-select-popper"
                    :disabled="!hasDateRange || loading.dutyOptions"
                    filterable
                    :placeholder="hasDateRange ? '请选择科室和姓名' : '请先选择请假日期'"
                  >
                    <el-option
                      v-for="person in dutyOptions"
                      :key="`${person.department}-${person.name}`"
                      :label="`${person.department} / ${person.name}`"
                      :value="`${person.department}|||${person.name}`"
                    />
                  </el-select>
                </label>

                <label class="form-field">
                  <span>请假事由 <em>*</em></span>
                  <el-input
                    v-model.trim="form.reason"
                    class="application-control application-reason-input"
                    type="textarea"
                    :rows="5"
                    maxlength="500"
                    placeholder="请输入请假事由"
                  />
                  <small class="field-counter">{{ form.reason.length }} / 500</small>
                </label>

                <div class="form-field">
                  <span>附件</span>
                  <div class="upload-control">
                    <label class="upload-button" :class="{ disabled: uploading }">
                      <input type="file" multiple :disabled="uploading" @change="handleFiles">
                      {{ uploading ? '正在上传...' : '选择文件' }}
                    </label>
                    <span class="upload-tip">单个文件不超过 20MB，最多 10 个</span>
                  </div>
                  <div v-if="attachments.length" class="attachment-list">
                    <div v-for="(file, index) in attachments" :key="file.url" class="attachment-item">
                      <span class="attachment-name" :title="file.name">{{ file.name }}</span>
                      <button type="button" aria-label="删除附件" @click="removeAttachment(index)">×</button>
                    </div>
                  </div>
                </div>

                <div class="form-actions">
                  <button type="submit" class="primary-button" :disabled="submitting || uploading">
                    {{ submitting ? '提交中...' : '提交申请' }}
                  </button>
                  <button type="button" class="secondary-button" @click="resetApplicationForm">清空</button>
                </div>
              </form>
            </section>

            <section class="list-panel">
              <div class="panel-heading">
                <div>
                  <h2>我的申请</h2>
                  <span>查看申请进度和审批结果</span>
                </div>
                <button type="button" class="icon-button" aria-label="刷新我的申请" title="刷新" @click="loadMyApplications">↻</button>
              </div>
              <div v-if="myError" class="inline-error">
                <span>{{ myError }}</span>
                <button type="button" @click="loadMyApplications">重试</button>
              </div>
              <div v-else-if="loading.my" class="table-state">正在加载我的申请...</div>
              <div v-else class="table-wrap">
                <table class="data-table compact-table">
                  <thead>
                    <tr>
                      <th>流程</th>
                      <th>科室 / 姓名</th>
                      <th>请假日期</th>
                      <th>状态</th>
                      <th class="table-action-column">操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in myRows" :key="row.id">
                      <td class="strong-cell">{{ row.processName || '-' }}</td>
                      <td>{{ personLabel(row) }}</td>
                      <td>{{ dateRangeLabel(row) }}</td>
                      <td><span :class="['status-chip', statusClass(row.approvalStatus)]">{{ approvalText(row.approvalStatus) }}</span></td>
                      <td class="table-action-column action-group">
                        <button type="button" class="text-button" @click="openMyDetail(row)">详情</button>
                        <button v-if="row.approvalStatus === '0'" type="button" class="text-button danger-text" @click="withdraw(row)">撤销</button>
                        <button v-else type="button" class="text-button" @click="refill(row)">重新填写</button>
                      </td>
                    </tr>
                    <tr v-if="!myRows.length">
                      <td colspan="5" class="empty-cell">还没有提交过申请</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div class="table-footer">
                <span>共 {{ myTotal }} 条</span>
                <div class="pagination-actions">
                  <button type="button" class="page-button" :disabled="myQuery.pageNum <= 1" @click="changePage('my', -1)">上一页</button>
                  <span>第 {{ myQuery.pageNum }} / {{ myPageCount }} 页</span>
                  <button type="button" class="page-button" :disabled="myQuery.pageNum >= myPageCount" @click="changePage('my', 1)">下一页</button>
                </div>
              </div>
            </section>
          </div>
        </section>
    </main>

    <div v-if="detailOpen" class="modal-backdrop" @click.self="closeDetail">
      <section class="detail-modal" role="dialog" aria-modal="true" aria-labelledby="detail-title">
        <header class="detail-header">
          <div>
            <span class="detail-kicker">{{ detailMode === 'review' ? '待办处理' : '申请详情' }}</span>
            <h2 id="detail-title">{{ detailMode === 'review' ? '审核申请' : '申请详情' }}</h2>
          </div>
          <button type="button" class="modal-close" aria-label="关闭详情" @click="closeDetail">×</button>
        </header>

        <div v-if="detailLoading" class="modal-state">正在加载详情...</div>
        <div v-else-if="detail" class="detail-content">
          <div class="detail-grid">
            <div><span>流程</span><strong>{{ detail.processName || '-' }}</strong></div>
            <div><span>状态</span><strong>{{ approvalText(detail.approvalStatus) }}</strong></div>
            <div><span>审批通过后状态</span><strong>{{ detail.leaveStatus || '-' }}</strong></div>
            <div><span>申请人</span><strong>{{ detail.applicantName || '-' }}</strong></div>
            <div><span>科室 / 姓名</span><strong>{{ personLabel(detail) }}</strong></div>
            <div><span>请假日期</span><strong>{{ dateRangeLabel(detail) }}</strong></div>
            <div><span>申请时间</span><strong>{{ detail.createTime || '-' }}</strong></div>
            <div><span>最近处理时间</span><strong>{{ detail.updateTime || '-' }}</strong></div>
            <div><span>审批完成时间</span><strong>{{ detail.approvedTime || '-' }}</strong></div>
            <div class="detail-reason"><span>事由</span><strong>{{ detail.reason || '-' }}</strong></div>
          </div>

          <section class="flow-section">
            <div class="detail-section-heading">
              <h3>审批流程</h3>
              <span>{{ detailFlow.length }} 个节点</span>
            </div>
            <div v-if="detailFlow.length" class="flow-scroll">
              <div class="flow-track">
                <div v-for="(node, index) in detailFlow" :key="`detail-flow-${node.nodeId || index}`" class="flow-step">
                  <div :class="['flow-card', flowStateClass(node.state)]">
                    <div class="flow-index">{{ index + 1 }}</div>
                    <h4>{{ node.nodeName || `审批节点${index + 1}` }}</h4>
                    <p class="flow-role">{{ node.roleName || '未设置角色' }}</p>
                    <p class="flow-key">{{ node.roleKey || '-' }}</p>
                    <div class="flow-users">
                      <span>角色成员</span>
                      <div v-if="node.roleUsers && node.roleUsers.length" class="flow-user-list">
                        <i v-for="user in node.roleUsers" :key="user.userId || user.userName">
                          {{ user.displayName || user.nickName || user.userName }}
                        </i>
                      </div>
                      <em v-else>暂无用户</em>
                    </div>
                    <b :class="['flow-status', statusClass(node.state)]">{{ stateText(node) }}</b>
                    <p class="flow-operator">操作人：{{ node.approverName || (node.state === 'pending' ? '待处理' : '-') }}</p>
                    <p v-if="node.comment" class="flow-detail" :title="node.comment">意见：{{ node.comment }}</p>
                    <p v-if="node.time" class="flow-detail">时间：{{ node.time }}</p>
                  </div>
                  <span v-if="index < detailFlow.length - 1" class="flow-arrow">→</span>
                </div>
              </div>
            </div>
            <div v-else class="modal-empty">暂无审批节点</div>
          </section>

          <section v-if="nextNode" class="next-node">
            <span class="next-node-label">下一节点</span>
            <strong>{{ nextNode.nodeName || '-' }}</strong>
            <span>{{ nextNode.roleName || '未设置角色' }}</span>
            <small>角色成员：{{ roleUserNames(nextNode.roleUsers) || '暂无用户' }}</small>
          </section>

          <section v-if="detailMode === 'review'" class="review-section">
            <label class="form-field">
              <span>审批意见</span>
              <el-input
                v-model.trim="approvalComment"
                class="review-comment-input"
                type="textarea"
                :rows="2"
                maxlength="500"
                placeholder="请输入审批意见（可选）"
              />
            </label>
          </section>
        </div>

        <footer v-if="detailMode === 'review' && detail" class="detail-footer">
          <button type="button" class="secondary-button" :disabled="approvalSubmitting" @click="closeDetail">取消</button>
          <button type="button" class="danger-button" :disabled="approvalSubmitting" @click="handleApproval('reject')">拒绝</button>
          <button type="button" class="primary-button" :disabled="approvalSubmitting" @click="handleApproval('approve')">通过</button>
        </footer>
      </section>
    </div>

    <div v-if="notice.message" :class="['notice-toast', `notice-toast--${notice.type}`]">{{ notice.message }}</div>
  </div>
</template>

<script>
import { clearAuthState, getUserProfile } from '@/utils/auth'
import { getLeaveStatusOptions } from '@/utils/leave'
import {
  addLeaveApplication,
  approveLeaveApplication,
  getLeaveDutyOptions,
  getMyLeaveApplication,
  getTodoLeaveApplication,
  listLeaveProcess,
  listLeaveTodo,
  listMyLeaveApplication,
  uploadLeaveAttachment,
  withdrawLeaveApplication
} from '@/api/leave'

function createForm() {
  return {
    id: null,
    processId: null,
    leaveStatus: '',
    startDate: '',
    endDate: '',
    personKey: '',
    reason: ''
  }
}

function parseApprovalRecords(value) {
  if (Array.isArray(value)) return value
  if (!value) return []
  try {
    const records = JSON.parse(value)
    return Array.isArray(records) ? records : []
  } catch (error) {
    return []
  }
}

export default {
  name: 'ApplicationView',
  data() {
    return {
      activeView: 'approval',
      userProfile: getUserProfile(),
      backgroundImage: require('@/assets/bj.png'),
      topBanner: require('@/assets/top.png'),
      loading: {
        approval: false,
        my: false,
        processes: false,
        dutyOptions: false
      },
      approvalRows: [],
      approvalTotal: 0,
      approvalQuery: { pageNum: 1, pageSize: 10, department: '', name: '' },
      approvalError: '',
      myRows: [],
      myTotal: 0,
      myQuery: { pageNum: 1, pageSize: 10 },
      myError: '',
      processes: [],
      processError: '',
      dutyOptions: [],
      form: createForm(),
      attachments: [],
      uploading: false,
      submitting: false,
      detailOpen: false,
      detailLoading: false,
      detailMode: '',
      detail: null,
      approvalComment: '',
      approvalSubmitting: false,
      approvalDecision: '',
      notice: { message: '', type: 'success' },
      noticeTimer: null
    }
  },
  computed: {
    userLabel() {
      return this.userProfile.nickName || this.userProfile.userName || '当前用户'
    },
    currentViewTitle() {
      return this.activeView === 'approval' ? '流程审批' : '申请'
    },
    currentViewDescription() {
      return this.activeView === 'approval'
        ? '处理当前待办请假事项'
        : '在线填写、提交并跟踪个人申请'
    },
    hasDateRange() {
      return Boolean(this.form.startDate && this.form.endDate)
    },
    leaveStatusOptions() {
      return getLeaveStatusOptions(this.processes.find(process => process.id === this.form.processId))
    },
    approvalPageCount() {
      return Math.max(1, Math.ceil(this.approvalTotal / this.approvalQuery.pageSize))
    },
    myPageCount() {
      return Math.max(1, Math.ceil(this.myTotal / this.myQuery.pageSize))
    },
    detailFlow() {
      if (!this.detail) return []
      if (Array.isArray(this.detail.approvalFlow) && this.detail.approvalFlow.length) {
        return this.detail.approvalFlow
      }
      return parseApprovalRecords(this.detail.approvalRecords).map((record, index) => ({
        nodeId: record.nodeId,
        nodeOrder: record.nodeOrder || index + 1,
        nodeName: record.nodeName,
        roleKey: record.roleKey,
        roleName: record.roleName,
        roleUsers: [],
        state: record.decision === 'reject' || record.result === '拒绝' || record.result === '驳回' ? 'rejected' : 'approved',
        result: record.result,
        comment: record.comment,
        time: record.time,
        approverName: record.approverName
      }))
    },
    nextNode() {
      if (this.detail && this.detail.nextNode) return this.detail.nextNode
      return this.detailFlow.find(node => node.state === 'pending') || null
    }
  },
  created() {
    this.loadApproval()
    this.loadProcesses()
  },
  beforeDestroy() {
    window.clearTimeout(this.noticeTimer)
  },
  methods: {
    setActiveView(view) {
      this.activeView = view
      if (view === 'approval') {
        this.loadApproval()
      } else {
        this.loadMyApplications()
        if (!this.processes.length) this.loadProcesses()
      }
    },
    goDashboard() {
      this.$router.push('/')
    },
    handleLogout() {
      if (!window.confirm('确定退出当前前台账号吗？')) return
      clearAuthState()
      this.$router.replace('/login')
    },
    loadApproval() {
      this.loading.approval = true
      this.approvalError = ''
      listLeaveTodo(this.approvalQuery).then(result => {
        this.approvalRows = result.rows || []
        this.approvalTotal = Number(result.total) || 0
      }).catch(error => {
        this.approvalError = error.message || '流程审批列表加载失败'
      }).finally(() => {
        this.loading.approval = false
      })
    },
    searchApproval() {
      this.approvalQuery.pageNum = 1
      this.loadApproval()
    },
    loadMyApplications() {
      this.loading.my = true
      this.myError = ''
      listMyLeaveApplication(this.myQuery).then(result => {
        this.myRows = result.rows || []
        this.myTotal = Number(result.total) || 0
      }).catch(error => {
        this.myError = error.message || '我的申请加载失败'
      }).finally(() => {
        this.loading.my = false
      })
    },
    loadProcesses() {
      this.loading.processes = true
      this.processError = ''
      listLeaveProcess({ pageNum: 1, pageSize: 1000, status: '0' }).then(result => {
        this.processes = result.rows || []
        this.syncLeaveStatus()
      }).catch(error => {
        this.processError = error.message || '请假流程加载失败'
      }).finally(() => {
        this.loading.processes = false
      })
    },
    handleProcessChange() {
      this.form.leaveStatus = ''
      this.syncLeaveStatus()
    },
    syncLeaveStatus() {
      const options = this.leaveStatusOptions
      if (!options.includes(this.form.leaveStatus)) {
        this.form.leaveStatus = options.length === 1 ? options[0] : ''
      }
    },
    loadDutyOptionsFromForm() {
      this.dutyOptions = []
      this.form.personKey = ''
      if (!this.hasDateRange) return
      if (this.form.startDate > this.form.endDate) {
        this.showNotice('开始日期不能晚于结束日期', 'error')
        return
      }
      this.loading.dutyOptions = true
      getLeaveDutyOptions(this.form.startDate, this.form.endDate).then(result => {
        this.dutyOptions = result.data || []
      }).catch(error => {
        this.showNotice(error.message || '可请假人员加载失败', 'error')
      }).finally(() => {
        this.loading.dutyOptions = false
      })
    },
    resetApplicationForm() {
      this.form = createForm()
      this.attachments = []
      this.dutyOptions = []
    },
    validateApplication() {
      if (!this.form.processId) return '请选择请假流程'
      if (!this.form.leaveStatus) return '请选择审批通过后状态'
      if (!this.leaveStatusOptions.includes(this.form.leaveStatus)) return '请重新选择审批通过后状态'
      if (!this.form.startDate || !this.form.endDate) return '请选择请假日期'
      if (this.form.startDate > this.form.endDate) return '开始日期不能晚于结束日期'
      if (!this.form.personKey) return '请选择科室和姓名'
      if (!this.form.reason) return '请填写请假事由'
      return ''
    },
    submitApplication() {
      const validationMessage = this.validateApplication()
      if (validationMessage) {
        this.showNotice(validationMessage, 'error')
        return
      }
      const parts = this.form.personKey.split('|||')
      this.submitting = true
      addLeaveApplication({
        processId: this.form.processId,
        leaveStatus: this.form.leaveStatus,
        department: parts[0],
        name: parts[1],
        startDate: this.form.startDate,
        endDate: this.form.endDate,
        reason: this.form.reason,
        attachments: this.attachments.map(file => file.url).join(',')
      }).then(() => {
        this.showNotice('申请已提交')
        this.resetApplicationForm()
        this.myQuery.pageNum = 1
        this.loadMyApplications()
      }).catch(error => {
        this.showNotice(error.message || '申请提交失败', 'error')
      }).finally(() => {
        this.submitting = false
      })
    },
    async handleFiles(event) {
      const files = Array.from(event.target.files || [])
      event.target.value = ''
      if (!files.length) return
      if (this.attachments.length + files.length > 10) {
        this.showNotice('附件最多上传 10 个', 'error')
        return
      }
      this.uploading = true
      try {
        for (const file of files) {
          if (file.size > 20 * 1024 * 1024) {
            throw new Error(`${file.name} 超过 20MB 限制`)
          }
          const result = await uploadLeaveAttachment(file)
          const url = result.fileName || result.url || (result.data && (result.data.fileName || result.data.url))
          if (!url) throw new Error(`${file.name} 上传结果异常`)
          this.attachments.push({ name: file.name, url })
        }
        this.showNotice('附件上传成功')
      } catch (error) {
        this.showNotice(error.message || '附件上传失败', 'error')
      } finally {
        this.uploading = false
      }
    },
    removeAttachment(index) {
      this.attachments.splice(index, 1)
    },
    changePage(type, offset) {
      const query = type === 'approval' ? this.approvalQuery : this.myQuery
      const pageCount = type === 'approval' ? this.approvalPageCount : this.myPageCount
      const nextPage = Math.min(pageCount, Math.max(1, query.pageNum + offset))
      if (nextPage === query.pageNum) return
      query.pageNum = nextPage
      if (type === 'approval') this.loadApproval()
      else this.loadMyApplications()
    },
    openApprovalDetail(row) {
      this.openDetail('review', row.id, getTodoLeaveApplication)
    },
    openMyDetail(row) {
      this.openDetail('view', row.id, getMyLeaveApplication)
    },
    openDetail(mode, id, loader) {
      this.detailMode = mode
      this.detailOpen = true
      this.detailLoading = true
      this.detail = null
      this.approvalComment = ''
      loader(id).then(result => {
        this.detail = result.data
      }).catch(error => {
        this.showNotice(error.message || '申请详情加载失败', 'error')
        this.closeDetail()
      }).finally(() => {
        this.detailLoading = false
      })
    },
    closeDetail() {
      this.detailOpen = false
      this.detail = null
      this.detailMode = ''
      this.approvalComment = ''
    },
    handleApproval(decision) {
      if (!this.detail || this.approvalSubmitting) return
      const actionText = decision === 'approve' ? '通过' : '拒绝'
      if (!window.confirm(`确认${actionText}该请假申请吗？`)) return
      this.approvalSubmitting = true
      this.approvalDecision = decision
      approveLeaveApplication(this.detail.id, { decision, comment: this.approvalComment }).then(() => {
        this.showNotice(`已${actionText}`)
        this.closeDetail()
        this.loadApproval()
      }).catch(error => {
        this.showNotice(error.message || `申请${actionText}失败`, 'error')
      }).finally(() => {
        this.approvalSubmitting = false
        this.approvalDecision = ''
      })
    },
    withdraw(row) {
      if (!window.confirm('撤销后该申请将不再进入审批流程，是否继续？')) return
      withdrawLeaveApplication(row.id).then(() => {
        this.showNotice('申请已撤销')
        this.loadMyApplications()
      }).catch(error => {
        this.showNotice(error.message || '申请撤销失败', 'error')
      })
    },
    refill(row) {
      this.activeView = 'apply'
      this.form = {
        id: null,
        processId: row.processId,
        leaveStatus: row.leaveStatus || '',
        startDate: row.startDate || '',
        endDate: row.endDate || '',
        personKey: `${row.department || ''}|||${row.name || ''}`,
        reason: row.reason || ''
      }
      this.syncLeaveStatus()
      this.attachments = this.parseAttachments(row.attachments)
      this.loadDutyOptionsPreservingPerson()
      this.showNotice('已将申请内容带入表单，提交后会生成新的申请')
    },
    loadDutyOptionsPreservingPerson() {
      if (!this.hasDateRange || this.form.startDate > this.form.endDate) return
      const personKey = this.form.personKey
      this.loading.dutyOptions = true
      getLeaveDutyOptions(this.form.startDate, this.form.endDate).then(result => {
        this.dutyOptions = result.data || []
        this.form.personKey = personKey
      }).catch(error => {
        this.showNotice(error.message || '可请假人员加载失败', 'error')
      }).finally(() => {
        this.loading.dutyOptions = false
      })
    },
    parseAttachments(value) {
      if (!value) return []
      return String(value).split(',').filter(Boolean).map(url => ({
        name: url.split('/').pop() || url,
        url
      }))
    },
    personLabel(row) {
      if (!row) return '-'
      return `${row.department || '-'} / ${row.name || '-'}`
    },
    dateRangeLabel(row) {
      if (!row) return '-'
      return `${row.startDate || '-'} 至 ${row.endDate || '-'}`
    },
    approvalText(value) {
      return value === '1' ? '已通过' : value === '2' ? '已拒绝' : value === '3' ? '已撤销' : '审批中'
    },
    statusClass(value) {
      const state = value === '1' || value === 'approved' ? 'success' : value === '2' || value === 'rejected' ? 'danger' : value === '3' || value === 'withdrawn' ? 'muted' : value === 'pending' || value === '0' ? 'warning' : 'muted'
      return `status--${state}`
    },
    flowStateClass(state) {
      return `flow-card--${state || 'waiting'}`
    },
    stateText(node) {
      if (node.state === 'approved') return node.result || '通过'
      if (node.state === 'rejected') return node.result || '拒绝'
      if (node.state === 'pending') return '待审批'
      if (node.state === 'withdrawn') return '已撤销'
      return '未到达'
    },
    roleUserNames(users) {
      return (users || [])
        .map(user => user.displayName || user.nickName || user.userName)
        .filter(Boolean)
        .join('、')
    },
    showNotice(message, type = 'success') {
      window.clearTimeout(this.noticeTimer)
      this.notice = { message, type }
      this.noticeTimer = window.setTimeout(() => {
        this.notice = { message: '', type: 'success' }
      }, 3200)
    }
  }
}
</script>

<style scoped>
.application-page {
  min-height: 100vh;
  color: #213247;
  background: #edf2f7;
}

.application-header {
  height: 72px;
  padding: 0 34px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #eef8ff;
  background: #183b61;
  box-sizing: border-box;
}

.application-brand,
.application-header-actions,
.application-user,
.application-heading,
.workspace-toolbar,
.panel-heading,
.toolbar-actions,
.form-field-group,
.form-actions,
.upload-control,
.attachment-item,
.table-footer,
.pagination-actions,
.detail-header,
.detail-footer,
.next-node {
  display: flex;
  align-items: center;
}

.application-brand {
  gap: 12px;
}

.application-brand-mark {
  width: 38px;
  height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(128, 216, 255, 0.62);
  border-radius: 8px;
  color: #a9edff;
  font-size: 20px;
  font-weight: 700;
  background: #125493;
}

.application-brand strong,
.application-brand small {
  display: block;
}

.application-brand strong {
  font-size: 20px;
  letter-spacing: 1px;
}

.application-brand small {
  margin-top: 4px;
  color: #9cc4e6;
  font-size: 12px;
}

.application-header-actions {
  gap: 16px;
}

.application-user {
  gap: 7px;
  color: #c9e2f7;
  font-size: 13px;
}

.application-user::before {
  content: '';
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #55d693;
  box-shadow: 0 0 0 4px rgba(85, 214, 147, 0.14);
}

.header-action,
.back-dashboard-button,
.sidebar-nav,
.small-link,
.icon-button,
.outline-button,
.text-button,
.page-button,
.modal-close,
.upload-button,
.primary-button,
.secondary-button,
.danger-button,
.attachment-item button,
.inline-error button {
  border: 0;
  font: inherit;
  cursor: pointer;
}

.header-action {
  padding: 8px 0;
  color: #e7f5ff;
  font-size: 14px;
  background: transparent;
}

.header-action:hover,
.small-link:hover,
.text-button:hover {
  color: #1879d1;
}

.application-layout {
  min-height: calc(100vh - 72px);
  display: flex;
}

.application-sidebar {
  width: 230px;
  flex: 0 0 230px;
  padding: 30px 14px 20px;
  position: relative;
  color: #d7e4ef;
  background: #26384c;
  box-sizing: border-box;
}

.sidebar-title {
  margin: 0 20px 22px;
  color: #8ed2ff;
  font-size: 14px;
  letter-spacing: 1px;
}

.sidebar-nav {
  width: 100%;
  min-height: 52px;
  padding: 0 18px;
  display: flex;
  align-items: center;
  gap: 13px;
  border-left: 3px solid transparent;
  color: #cfdae6;
  text-align: left;
  background: transparent;
}

.sidebar-nav:hover,
.sidebar-nav.active {
  border-left-color: #38a9ff;
  color: #ffffff;
  background: #1d2e41;
}

.sidebar-nav-icon {
  width: 21px;
  color: #b7cbe0;
  font-size: 19px;
  text-align: center;
}

.sidebar-nav.active .sidebar-nav-icon {
  color: #52b7ff;
}

.sidebar-nav-badge {
  min-width: 20px;
  height: 20px;
  margin-left: auto;
  padding: 0 5px;
  border-radius: 10px;
  color: #ffffff;
  font-size: 11px;
  line-height: 20px;
  text-align: center;
  background: #e8785d;
  box-sizing: border-box;
}

.sidebar-footer {
  position: absolute;
  right: 20px;
  bottom: 24px;
  left: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #7791a9;
  font-size: 12px;
}

.sidebar-footer-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #56d18e;
}

.application-main {
  min-width: 0;
  flex: 1;
  padding: 32px 38px 44px;
  overflow: auto;
  box-sizing: border-box;
}

.application-heading {
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 26px;
}

.application-breadcrumb {
  margin-bottom: 10px;
  color: #7e91a4;
  font-size: 13px;
}

.application-heading h1 {
  margin: 0;
  color: #213247;
  font-size: 28px;
  font-weight: 650;
}

.application-heading p {
  margin: 8px 0 0;
  color: #76889a;
  font-size: 14px;
}

.back-dashboard-button {
  flex: 0 0 auto;
  min-height: 38px;
  padding: 0 18px;
  border: 1px solid #bed2e4;
  border-radius: 5px;
  color: #28638e;
  font-size: 13px;
  background: #ffffff;
}

.back-dashboard-button:hover,
.outline-button:hover,
.secondary-button:hover,
.page-button:hover:not(:disabled) {
  border-color: #52a9e5;
  color: #126baa;
  background: #f5fbff;
}

.workspace-panel,
.form-panel,
.list-panel {
  border: 1px solid #d9e2eb;
  border-radius: 6px;
  background: #ffffff;
  box-shadow: 0 8px 24px rgba(40, 69, 96, 0.06);
}

.workspace-toolbar {
  min-height: 82px;
  padding: 18px 22px;
  justify-content: space-between;
  gap: 20px;
  border-bottom: 1px solid #e8eef4;
  box-sizing: border-box;
}

.workspace-toolbar h2,
.panel-heading h2,
.detail-header h2,
.detail-section-heading h3 {
  margin: 0;
  color: #25384c;
  font-weight: 650;
}

.workspace-toolbar h2,
.panel-heading h2 {
  font-size: 17px;
}

.toolbar-caption,
.panel-heading span {
  display: block;
  margin-top: 6px;
  color: #91a1b1;
  font-size: 12px;
}

.toolbar-actions {
  gap: 9px;
}

.toolbar-field {
  display: flex;
  align-items: center;
  gap: 7px;
  color: #788b9e;
  font-size: 12px;
}

.toolbar-field input,
.form-field input,
.form-field select,
.form-field textarea {
  width: 100%;
  border: 1px solid #cfdae4;
  border-radius: 4px;
  outline: none;
  color: #2c4054;
  font: inherit;
  background: #ffffff;
  box-sizing: border-box;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.toolbar-field input {
  width: 118px;
  height: 34px;
  padding: 0 9px;
  font-size: 13px;
}

.form-field input,
.form-field select {
  height: 40px;
  padding: 0 11px;
  font-size: 13px;
}

.form-field textarea {
  min-height: 112px;
  padding: 10px 11px;
  resize: vertical;
  line-height: 1.55;
}

.toolbar-field input:focus,
.form-field input:focus,
.form-field select:focus,
.form-field textarea:focus {
  border-color: #55aee7;
  box-shadow: 0 0 0 3px rgba(85, 174, 231, 0.13);
}

.form-field input:disabled,
.form-field select:disabled {
  color: #9aaabb;
  cursor: not-allowed;
  background: #f4f7fa;
}

.outline-button,
.secondary-button,
.page-button {
  min-height: 34px;
  padding: 0 13px;
  border: 1px solid #c8d7e4;
  border-radius: 4px;
  color: #3e6889;
  font-size: 13px;
  background: #ffffff;
}

.icon-button {
  width: 34px;
  height: 34px;
  border: 1px solid #c8d7e4;
  border-radius: 4px;
  color: #3c7198;
  font-size: 20px;
  line-height: 1;
  background: #ffffff;
}

.icon-button:hover {
  border-color: #55aee7;
  color: #1476bd;
  background: #f5fbff;
}

.inline-error {
  margin: 14px 22px 0;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  border: 1px solid #f4c9c5;
  border-radius: 4px;
  color: #a44e48;
  font-size: 13px;
  background: #fff7f6;
}

.inline-error button {
  padding: 0;
  color: #a44e48;
  font-size: 12px;
  background: transparent;
}

.table-wrap {
  overflow-x: auto;
}

.data-table {
  width: 100%;
  min-width: 930px;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: 13px;
}

.compact-table {
  min-width: 680px;
}

.data-table th,
.data-table td {
  padding: 15px 14px;
  border-bottom: 1px solid #edf1f5;
  color: #64788c;
  text-align: left;
  vertical-align: middle;
}

.data-table th {
  color: #8193a5;
  font-size: 12px;
  font-weight: 500;
  background: #f8fafc;
}

.data-table tbody tr:hover {
  background: #fbfdff;
}

.strong-cell {
  color: #2e475f !important;
  font-weight: 600;
}

.reason-cell {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.table-action-column {
  width: 110px;
  text-align: center !important;
}

.action-group {
  white-space: nowrap;
}

.text-button,
.small-link {
  padding: 0;
  color: #1976bd;
  font-size: 13px;
  background: transparent;
}

.danger-text {
  margin-left: 10px;
  color: #c45a53;
}

.danger-text:hover {
  color: #a83d36 !important;
}

.table-state,
.empty-cell,
.modal-state,
.modal-empty {
  color: #98a7b6;
  text-align: center;
}

.table-state {
  padding: 66px 20px;
  font-size: 13px;
}

.empty-cell {
  padding: 66px 20px !important;
}

.table-footer {
  min-height: 58px;
  padding: 0 22px;
  justify-content: space-between;
  gap: 18px;
  color: #94a4b3;
  font-size: 12px;
  box-sizing: border-box;
}

.pagination-actions {
  gap: 10px;
}

.pagination-actions span {
  min-width: 88px;
  text-align: center;
}

.page-button {
  min-height: 30px;
  padding: 0 9px;
  font-size: 12px;
}

.page-button:disabled {
  color: #b5c0ca;
  cursor: not-allowed;
  background: #f6f8fa;
}

.apply-grid {
  display: grid;
  grid-template-columns: minmax(340px, 0.82fr) minmax(500px, 1.45fr);
  gap: 20px;
  align-items: start;
}

.form-panel,
.list-panel {
  min-width: 0;
  overflow: hidden;
}

.panel-heading {
  min-height: 76px;
  padding: 17px 20px;
  justify-content: space-between;
  gap: 18px;
  border-bottom: 1px solid #e8eef4;
  box-sizing: border-box;
}

.application-form {
  padding: 22px 20px 24px;
}

.form-field {
  position: relative;
  display: block;
  margin-bottom: 18px;
}

.form-field > span {
  display: block;
  margin-bottom: 8px;
  color: #50677d;
  font-size: 13px;
}

.form-field em {
  color: #d36c62;
  font-style: normal;
}

.form-field-group {
  gap: 12px;
}

.form-field-group .form-field {
  min-width: 0;
  flex: 1;
}

.field-counter {
  position: absolute;
  right: 10px;
  bottom: 9px;
  color: #9eacb9;
  font-size: 11px;
}

.upload-control {
  flex-wrap: wrap;
  gap: 9px;
}

.upload-button {
  position: relative;
  min-height: 32px;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  border: 1px solid #b9d3e7;
  border-radius: 4px;
  color: #286a98;
  font-size: 12px;
  cursor: pointer;
  background: #f7fbfe;
  box-sizing: border-box;
}

.upload-button:hover {
  border-color: #55aee7;
  background: #eef8ff;
}

.upload-button.disabled {
  cursor: wait;
  opacity: 0.65;
}

.upload-button input {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}

.upload-tip {
  color: #a0adba;
  font-size: 11px;
}

.attachment-list {
  margin-top: 9px;
}

.attachment-item {
  justify-content: space-between;
  gap: 10px;
  min-height: 30px;
  padding: 0 8px;
  border-bottom: 1px solid #edf2f6;
  color: #61778d;
  font-size: 12px;
}

.attachment-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.attachment-item button {
  flex: 0 0 auto;
  padding: 0 4px;
  color: #b26b65;
  font-size: 18px;
  background: transparent;
}

.form-actions {
  gap: 10px;
  margin-top: 26px;
}

.primary-button,
.danger-button {
  min-height: 38px;
  padding: 0 20px;
  border-radius: 4px;
  color: #ffffff;
  font-size: 13px;
}

.primary-button {
  background: #197ac1;
}

.primary-button:hover:not(:disabled) {
  background: #126baa;
}

.danger-button {
  background: #c9645c;
}

.danger-button:hover:not(:disabled) {
  background: #ae4f48;
}

.primary-button:disabled,
.danger-button:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.status-chip {
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  padding: 0 8px;
  border-radius: 12px;
  font-size: 12px;
  white-space: nowrap;
}

.status--success {
  color: #2a895c;
  background: #e8f7ef;
}

.status--warning {
  color: #a97828;
  background: #fff5de;
}

.status--danger {
  color: #b4524a;
  background: #fff0ee;
}

.status--muted {
  color: #758696;
  background: #eef2f5;
}

.modal-backdrop {
  position: fixed;
  z-index: 100;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  padding: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(17, 35, 54, 0.58);
  box-sizing: border-box;
}

.detail-modal {
  width: min(1120px, 100%);
  max-height: calc(100vh - 72px);
  overflow: auto;
  border-radius: 7px;
  background: #ffffff;
  box-shadow: 0 18px 50px rgba(12, 31, 51, 0.28);
}

.detail-header {
  min-height: 76px;
  padding: 15px 24px;
  justify-content: space-between;
  border-bottom: 1px solid #e8eef4;
  box-sizing: border-box;
}

.detail-kicker {
  display: block;
  margin-bottom: 5px;
  color: #86a0b5;
  font-size: 11px;
}

.detail-header h2 {
  font-size: 19px;
}

.modal-close {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  color: #8394a5;
  font-size: 25px;
  line-height: 1;
  background: transparent;
}

.modal-close:hover {
  color: #2c6d9a;
  background: #f0f5f8;
}

.modal-state {
  min-height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
}

.detail-content {
  padding: 22px 24px 28px;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px 20px;
}

.detail-grid > div {
  min-width: 0;
}

.detail-grid span,
.detail-grid strong {
  display: block;
}

.detail-grid span {
  margin-bottom: 6px;
  color: #91a0ad;
  font-size: 12px;
}

.detail-grid strong {
  overflow: hidden;
  color: #344c62;
  font-size: 13px;
  font-weight: 500;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.detail-grid .detail-reason {
  grid-column: 1 / -1;
}

.detail-grid .detail-reason strong {
  white-space: normal;
  line-height: 1.55;
}

.flow-section {
  margin-top: 28px;
}

.detail-section-heading {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 14px;
}

.detail-section-heading h3 {
  font-size: 15px;
}

.detail-section-heading span {
  color: #91a0ad;
  font-size: 12px;
}

.flow-scroll {
  overflow-x: auto;
  padding-bottom: 5px;
}

.flow-track {
  min-width: max-content;
  display: flex;
  align-items: stretch;
}

.flow-step {
  display: flex;
  align-items: center;
}

.flow-card {
  width: 220px;
  min-height: 276px;
  padding: 15px 14px 13px;
  border: 1px solid #d9e2eb;
  border-radius: 5px;
  text-align: center;
  box-sizing: border-box;
  background: #ffffff;
}

.flow-card--approved {
  border-color: #b7dfc8;
  background: #fbfffc;
}

.flow-card--rejected {
  border-color: #efc0bb;
  background: #fffafa;
}

.flow-card--pending {
  border-color: #9ec8e8;
  background: #f7fbff;
}

.flow-card--withdrawn {
  background: #fafbfc;
}

.flow-index {
  width: 30px;
  height: 30px;
  margin: 0 auto 9px;
  border-radius: 50%;
  color: #ffffff;
  line-height: 30px;
  font-size: 13px;
  font-weight: 600;
  background: #5b9ac5;
}

.flow-card--approved .flow-index {
  background: #55a875;
}

.flow-card--rejected .flow-index {
  background: #d36b64;
}

.flow-card--pending .flow-index {
  background: #2d8dd0;
  box-shadow: 0 0 0 4px #dceefa;
}

.flow-card h4 {
  margin: 0 0 7px;
  color: #344c62;
  font-size: 14px;
}

.flow-role,
.flow-key,
.flow-operator,
.flow-detail {
  margin: 0;
  color: #718699;
  font-size: 12px;
}

.flow-role,
.flow-key {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.flow-key {
  margin-top: 4px;
  color: #a0adb9;
}

.flow-users {
  min-height: 70px;
  margin: 12px 0 10px;
  padding: 8px;
  border-top: 1px solid #edf1f4;
  border-bottom: 1px solid #edf1f4;
  text-align: left;
}

.flow-users > span,
.flow-users > em {
  display: block;
  color: #9aa8b4;
  font-size: 11px;
  font-style: normal;
}

.flow-user-list {
  max-height: 42px;
  margin-top: 5px;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  overflow-y: auto;
}

.flow-user-list i {
  max-width: 100%;
  padding: 3px 6px;
  overflow: hidden;
  border-radius: 3px;
  color: #5e7387;
  font-size: 11px;
  font-style: normal;
  text-overflow: ellipsis;
  white-space: nowrap;
  background: #eef3f7;
}

.flow-status {
  display: inline-flex;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
}

.flow-operator {
  margin-top: 10px;
}

.flow-detail {
  margin-top: 5px;
  overflow: hidden;
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.flow-arrow {
  width: 48px;
  color: #a2b2c0;
  font-size: 25px;
  text-align: center;
}

.next-node {
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 22px;
  padding: 13px 15px;
  border-left: 3px solid #e3a149;
  color: #526a7e;
  background: #fffaf0;
}

.next-node-label {
  color: #b27b30;
  font-size: 12px;
  font-weight: 600;
}

.next-node strong {
  color: #445a6c;
  font-size: 13px;
}

.next-node small {
  flex-basis: 100%;
  color: #8494a2;
  font-size: 12px;
}

.review-section {
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #e9eef3;
}

.detail-footer {
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 24px;
  border-top: 1px solid #e8eef4;
}

.notice-toast {
  position: fixed;
  z-index: 120;
  top: 86px;
  left: 50%;
  max-width: min(420px, calc(100vw - 40px));
  padding: 11px 18px;
  border: 1px solid #bde2cd;
  border-radius: 4px;
  color: #287653;
  font-size: 13px;
  text-align: center;
  background: #f0fbf4;
  box-shadow: 0 8px 22px rgba(38, 74, 102, 0.15);
  transform: translateX(-50%);
}

.notice-toast--error {
  border-color: #efc4bf;
  color: #a44e48;
  background: #fff6f5;
}

@media (max-width: 1180px) {
  .application-main {
    padding-right: 24px;
    padding-left: 24px;
  }

  .workspace-toolbar {
    align-items: flex-start;
    flex-direction: column;
  }

  .toolbar-actions {
    flex-wrap: wrap;
  }

  .apply-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 720px) {
  .application-header {
    height: auto;
    min-height: 64px;
    padding: 12px 16px;
    align-items: flex-start;
    gap: 12px;
  }

  .application-brand strong {
    font-size: 17px;
  }

  .application-brand small,
  .application-user {
    display: none;
  }

  .application-header-actions {
    gap: 12px;
  }

  .application-layout {
    display: block;
  }

  .application-sidebar {
    width: 100%;
    padding: 10px 12px;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .sidebar-title,
  .sidebar-footer {
    display: none;
  }

  .sidebar-nav {
    min-height: 42px;
    padding: 0 12px;
    border-left: 0;
    border-bottom: 3px solid transparent;
  }

  .sidebar-nav:hover,
  .sidebar-nav.active {
    border-bottom-color: #38a9ff;
    border-left-color: transparent;
  }

  .application-main {
    padding: 22px 14px 30px;
  }

  .application-heading {
    align-items: flex-start;
    flex-direction: column;
  }

  .toolbar-actions {
    width: 100%;
  }

  .toolbar-field {
    min-width: 0;
    flex: 1;
  }

  .toolbar-field input {
    width: 100%;
  }

  .form-field-group {
    align-items: stretch;
    flex-direction: column;
    gap: 0;
  }

  .table-footer {
    padding: 0 14px;
    align-items: flex-start;
    flex-direction: column;
    justify-content: center;
  }

  .modal-backdrop {
    padding: 12px;
  }

  .detail-content {
    padding: 18px 15px 22px;
  }

  .detail-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 14px;
  }

  .detail-footer {
    padding: 12px 15px;
  }
}
</style>
<style scoped>
.application-page {
  position: relative;
  width: 100vw;
  height: 100vh;
  min-height: 0;
  overflow: hidden;
  color: #d9f4ff;
  background-color: #06193b;
  background-position: center top;
  background-repeat: no-repeat;
  background-size: 100% 100%;
}

.application-top-banner {
  position: absolute;
  top: -0.2vh;
  left: -1.5%;
  z-index: 1;
  width: 103%;
  height: auto;
  pointer-events: none;
}

.application-header {
  position: relative;
  z-index: 6;
  height: 8.8vh;
  padding: 0;
  display: block;
  border: 0;
  color: #e9f8ff;
  background: transparent;
  box-sizing: border-box;
}

.application-meta {
  position: absolute;
  top: 5vh;
  left: 1vw;
  color: #c9edff;
  font-size: clamp(11px, 0.72vw, 14px);
  letter-spacing: 0.04vw;
}

.application-page-actions {
  position: relative;
  z-index: 7;
  margin-bottom: 0.75vh;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5vw;
}

.application-tab,
.application-page-actions .header-action {
  height: 1.9vw;
  min-height: 30px;
  padding: 0 0.7vw;
  border: 1px solid rgba(72, 201, 255, 0.4);
  border-radius: 0.3vw;
  color: #dff7ff;
  font-size: clamp(12px, 0.7vw, 15px);
  line-height: 1;
  background: linear-gradient(180deg, rgba(7, 93, 211, 0.9), rgba(4, 58, 122, 0.7));
  box-shadow: inset 0 0 0.5vw rgba(91, 228, 255, 0.25);
  cursor: pointer;
  box-sizing: border-box;
}

.application-tab:hover,
.application-page-actions .header-action:hover,
.application-tab.active {
  border-color: rgba(112, 226, 255, 0.9);
  color: #ffffff;
  background: linear-gradient(180deg, rgba(16, 132, 239, 0.98), rgba(6, 80, 164, 0.88));
  box-shadow: 0 0 0.8vw rgba(49, 194, 255, 0.36), inset 0 0 0.5vw rgba(152, 241, 255, 0.28);
}

.application-tab-badge {
  min-width: 17px;
  height: 17px;
  margin-left: 5px;
  padding: 0 4px;
  display: inline-block;
  border-radius: 9px;
  color: #ffffff;
  font-size: 11px;
  line-height: 17px;
  background: #ec805c;
  box-sizing: border-box;
}

.application-main {
  position: relative;
  z-index: 2;
  width: 100%;
  height: calc(100vh - 8.8vh);
  min-width: 0;
  padding: 0.3vh 0.56vw 1.9vh 0.56vw;
  overflow: auto;
  box-sizing: border-box;
}

.application-heading {
  min-height: 5.2vh;
  margin: 0 0 0.7vh;
  padding: 0 0.7vw;
  display: flex;
  align-items: flex-start;
  color: #d7f3ff;
  box-sizing: border-box;
}

.application-breadcrumb {
  margin-bottom: 0.35vh;
  color: #74cfff;
  font-size: clamp(11px, 0.68vw, 14px);
}

.application-heading h1 {
  margin: 0;
  color: #ecfbff;
  font-size: clamp(18px, 1.28vw, 27px);
  font-weight: 700;
  text-shadow: 0 0 0.7vw rgba(63, 185, 255, 0.25);
}

.application-heading p {
  margin: 0.35vh 0 0;
  color: #9bd8f4;
  font-size: clamp(11px, 0.7vw, 14px);
}

.workspace-panel,
.form-panel,
.list-panel {
  border: 1px solid rgba(82, 197, 255, 0.42);
  border-radius: 0.42vw;
  background: linear-gradient(180deg, rgba(5, 48, 112, 0.83), rgba(3, 28, 76, 0.88));
  box-shadow: 0 0.6vw 1.4vw rgba(0, 9, 38, 0.28), inset 0 0 1vw rgba(78, 211, 255, 0.08);
}

.workspace-toolbar,
.panel-heading {
  min-height: 5.8vh;
  padding: 0.8vh 1vw;
  border-bottom: 1px solid rgba(102, 204, 255, 0.2);
  background: rgba(2, 29, 77, 0.32);
  box-sizing: border-box;
}

.workspace-toolbar h2,
.panel-heading h2,
.detail-header h2,
.detail-section-heading h3 {
  color: #e8fbff;
  font-size: clamp(15px, 0.9vw, 19px);
  text-shadow: 0 0 0.5vw rgba(68, 199, 255, 0.22);
}

.toolbar-caption,
.panel-heading span {
  margin-top: 0.32vh;
  color: #87c8e7;
  font-size: clamp(10px, 0.62vw, 13px);
}

.toolbar-field,
.form-field > span {
  color: #9dd9f5;
  font-size: clamp(11px, 0.66vw, 14px);
}

.toolbar-field input,
.form-field input,
.form-field select,
.form-field textarea {
  border-color: rgba(105, 216, 255, 0.34);
  color: #e3f7ff;
  background: rgba(2, 35, 91, 0.66);
  box-shadow: inset 0 0 0.55vw rgba(35, 168, 255, 0.08);
}

.toolbar-field input::placeholder,
.form-field input::placeholder,
.form-field textarea::placeholder {
  color: rgba(181, 222, 240, 0.55);
}

.toolbar-field input:focus,
.form-field input:focus,
.form-field select:focus,
.form-field textarea:focus {
  border-color: rgba(122, 232, 255, 0.82);
  box-shadow: 0 0 0 0.16vw rgba(64, 191, 255, 0.16), inset 0 0 0.55vw rgba(35, 168, 255, 0.1);
}

.form-field input:disabled,
.form-field select:disabled {
  color: rgba(192, 222, 234, 0.48);
  background: rgba(2, 25, 65, 0.58);
}

.outline-button,
.secondary-button,
.page-button,
.icon-button,
.upload-button {
  border-color: rgba(86, 203, 255, 0.45);
  color: #c9f1ff;
  background: linear-gradient(180deg, rgba(17, 112, 205, 0.78), rgba(4, 57, 126, 0.82));
  box-shadow: inset 0 0 0.5vw rgba(103, 225, 255, 0.13);
}

.outline-button:hover,
.secondary-button:hover,
.page-button:hover:not(:disabled),
.icon-button:hover,
.upload-button:hover {
  border-color: rgba(130, 236, 255, 0.88);
  color: #ffffff;
  background: linear-gradient(180deg, rgba(24, 145, 236, 0.9), rgba(7, 75, 158, 0.88));
}

.inline-error {
  border-color: rgba(255, 145, 119, 0.55);
  color: #ffd0bc;
  background: rgba(106, 36, 47, 0.54);
}

.inline-error button {
  color: #ffd0bc;
}

.data-table {
  min-width: 860px;
  color: #c7eafa;
  font-size: clamp(11px, 0.66vw, 14px);
}

.compact-table {
  min-width: 650px;
}

.data-table th,
.data-table td {
  padding: 1.05vh 0.85vw;
  border-bottom-color: rgba(105, 205, 255, 0.15);
  color: #a6d7ed;
}

.data-table th {
  color: #8ed2ed;
  background: rgba(4, 64, 137, 0.3);
}

.data-table tbody tr:hover {
  background: rgba(32, 155, 222, 0.12);
}

.strong-cell {
  color: #effcff !important;
}

.text-button,
.small-link {
  color: #65d8ff;
}

.text-button:hover,
.small-link:hover {
  color: #ffffff;
}

.danger-text {
  color: #ffae91;
}

.table-state,
.empty-cell,
.modal-state,
.modal-empty {
  color: #8fbcd2;
}

.table-footer {
  min-height: 4.8vh;
  padding: 0 1vw;
  color: #8ebfd7;
}

.pagination-actions span {
  color: #9ed5ed;
}

.page-button:disabled {
  border-color: rgba(87, 161, 199, 0.24);
  color: rgba(180, 218, 234, 0.42);
  background: rgba(6, 42, 91, 0.56);
}

.field-counter,
.upload-tip {
  color: #7db9d4;
}

.attachment-item {
  border-bottom-color: rgba(105, 205, 255, 0.16);
  color: #b8e1f2;
}

.attachment-item button {
  color: #ffb49d;
}

.status--success {
  color: #a6f4c6;
  background: rgba(37, 164, 104, 0.28);
}

.status--warning {
  color: #ffe19a;
  background: rgba(201, 133, 34, 0.28);
}

.status--danger {
  color: #ffc1ad;
  background: rgba(192, 74, 67, 0.3);
}

.status--muted {
  color: #b6d0dd;
  background: rgba(109, 151, 175, 0.24);
}

.modal-backdrop {
  background: rgba(1, 13, 38, 0.76);
}

.detail-modal {
  border: 1px solid rgba(82, 197, 255, 0.55);
  border-radius: 0.5vw;
  background: linear-gradient(180deg, rgba(5, 53, 120, 0.98), rgba(3, 26, 72, 0.98));
  box-shadow: 0 0.9vw 2vw rgba(0, 3, 19, 0.44), inset 0 0 1.2vw rgba(78, 211, 255, 0.09);
}

.detail-header,
.detail-footer {
  border-color: rgba(102, 204, 255, 0.22);
}

.detail-kicker,
.detail-grid span,
.detail-section-heading span,
.flow-users > span,
.flow-users > em {
  color: #86c2dc;
}

.detail-grid strong,
.flow-card h4,
.next-node strong {
  color: #e6faff;
}

.flow-card {
  border-color: rgba(91, 202, 255, 0.38);
  background: rgba(3, 34, 83, 0.82);
}

.flow-card--approved {
  border-color: rgba(101, 222, 155, 0.58);
  background: rgba(19, 94, 88, 0.34);
}

.flow-card--rejected {
  border-color: rgba(255, 137, 118, 0.6);
  background: rgba(108, 41, 60, 0.36);
}

.flow-card--pending {
  border-color: rgba(100, 218, 255, 0.78);
  background: rgba(9, 76, 139, 0.5);
}

.flow-card--withdrawn {
  background: rgba(22, 48, 79, 0.7);
}

.flow-role,
.flow-key,
.flow-operator,
.flow-detail,
.next-node,
.next-node small {
  color: #9bc6da;
}

.flow-users {
  border-color: rgba(102, 204, 255, 0.18);
}

.flow-user-list i {
  color: #c5e6f4;
  background: rgba(64, 149, 192, 0.28);
}

.flow-arrow {
  color: #60c8ed;
}

.next-node {
  border-color: rgba(232, 174, 74, 0.58);
  border-left-color: #e6a345;
  background: rgba(121, 77, 26, 0.3);
}

.next-node-label {
  color: #ffd180;
}

.notice-toast {
  top: 10vh;
  border-color: rgba(112, 229, 177, 0.55);
  color: #b7ffdb;
  background: rgba(13, 95, 83, 0.92);
  box-shadow: 0 0.6vw 1.5vw rgba(0, 16, 47, 0.38), inset 0 0 0.7vw rgba(137, 255, 224, 0.1);
}

.notice-toast--error {
  border-color: rgba(255, 145, 119, 0.62);
  color: #ffd0bc;
  background: rgba(111, 38, 50, 0.95);
}

@media (max-width: 720px) {
  .application-meta {
    display: none;
  }

  .application-page-actions {
    width: 100%;
    margin-bottom: 1vh;
    flex-wrap: wrap;
    justify-content: center;
    gap: 5px;
  }

  .application-tab,
  .application-page-actions .header-action {
    min-height: 28px;
    height: 28px;
    padding: 0 9px;
    border-radius: 4px;
    font-size: 12px;
  }

  .application-main {
    height: calc(100vh - 8.8vh);
    padding: 0.8vh 2vw 2vh;
  }

  .application-heading {
    padding: 0 1vw;
  }

  .workspace-toolbar {
    align-items: flex-start;
    flex-direction: column;
  }

  .toolbar-actions {
    width: 100%;
    flex-wrap: wrap;
  }

  .toolbar-field {
    min-width: 0;
    flex: 1;
  }

  .toolbar-field input {
    width: 100%;
  }
}
</style>

<style>
.application-page .application-control.el-select,
.application-page .application-control.el-date-editor,
.application-page .application-control.el-input {
  width: 100%;
  font-size: inherit;
}

.application-page .application-control .el-input__inner,
.application-page .application-control .el-textarea__inner {
  width: 100%;
  border: 1px solid rgba(105, 216, 255, 0.34);
  border-radius: 4px;
  outline: none;
  color: #e3f7ff;
  font: inherit;
  background: rgba(2, 35, 91, 0.66);
  box-shadow: inset 0 0 0.55vw rgba(35, 168, 255, 0.08);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.application-page .application-control .el-input__inner {
  height: 40px;
  padding: 0 34px 0 11px;
  line-height: 40px;
}

.application-page .application-control.el-date-editor .el-input__inner {
  padding-right: 11px;
  padding-left: 44px;
}

.application-page .application-control .el-textarea__inner {
  min-height: 112px;
  padding: 10px 11px;
  resize: vertical;
  line-height: 1.55;
}

.application-page .application-control .el-input__inner::placeholder,
.application-page .application-control .el-textarea__inner::placeholder {
  color: rgba(181, 222, 240, 0.55);
}

.application-page .application-control .el-input__inner:focus,
.application-page .application-control .el-textarea__inner:focus {
  border-color: rgba(122, 232, 255, 0.82);
  box-shadow: 0 0 0 0.16vw rgba(64, 191, 255, 0.16), inset 0 0 0.55vw rgba(35, 168, 255, 0.1);
}

.application-page .application-control .el-input__suffix,
.application-page .application-control .el-input__prefix,
.application-page .application-control .el-input__icon {
  color: #a6d9ef;
}

.application-page .application-control .el-input.is-disabled .el-input__inner,
.application-page .application-control.is-disabled .el-input__inner {
  color: rgba(192, 222, 234, 0.48);
  border-color: rgba(105, 216, 255, 0.2);
  background: rgba(2, 25, 65, 0.58);
}

.application-page .application-control .el-input.is-disabled .el-input__icon,
.application-page .application-control.is-disabled .el-input__icon {
  color: rgba(169, 208, 224, 0.34);
}

.application-select-popper,
.application-date-popper {
  border: 1px solid rgba(82, 197, 255, 0.55);
  border-radius: 4px;
  color: #c8edff;
  background: #06275d;
  box-shadow: 0 0.6vw 1.4vw rgba(0, 9, 38, 0.42), inset 0 0 1vw rgba(78, 211, 255, 0.08);
}

.application-select-popper .el-select-dropdown__item {
  color: #b8e3f5;
  background: transparent;
}

.application-select-popper .el-select-dropdown__item.hover,
.application-select-popper .el-select-dropdown__item:hover {
  color: #ffffff;
  background: rgba(35, 150, 225, 0.36);
}

.application-select-popper .el-select-dropdown__item.selected {
  color: #6de0ff;
  font-weight: 600;
  background: rgba(28, 124, 205, 0.3);
}

.application-select-popper .el-select-dropdown__empty {
  color: #8fbcd2;
}

.application-date-popper .el-picker-panel__body,
.application-date-popper .el-picker-panel__footer {
  background: #06275d;
}

.application-date-popper .el-picker-panel__footer {
  border-top-color: rgba(102, 204, 255, 0.2);
}

.application-date-popper .el-date-picker__header-label,
.application-date-popper .el-picker-panel__icon-btn,
.application-date-popper .el-date-table th {
  color: #c8edff;
}

.application-date-popper .el-date-table td.available {
  color: #b8e3f5;
}

.application-date-popper .el-date-table td.prev-month,
.application-date-popper .el-date-table td.next-month {
  color: rgba(143, 188, 210, 0.42);
}

.application-date-popper .el-date-table td.today span {
  color: #6de0ff;
}

.application-date-popper .el-date-table td.current:not(.disabled) span {
  color: #ffffff;
  background: #168ed4;
}

.application-date-popper .el-date-table td.available:hover span {
  color: #ffffff;
  background: rgba(35, 150, 225, 0.55);
}

.application-date-popper .el-picker-panel__icon-btn:hover,
.application-date-popper .el-date-picker__header-label:hover {
  color: #6de0ff;
}

.application-date-popper .el-button--text {
  color: #6de0ff;
}

.application-page .review-comment-input {
  display: block;
  width: 100%;
}

.application-page .review-comment-input .el-textarea__inner {
  display: block;
  width: 100%;
  min-height: 0;
  padding: 8px 11px;
  border: 1px solid rgba(105, 216, 255, 0.34);
  border-radius: 4px;
  outline: none;
  box-sizing: border-box;
  color: #e3f7ff;
  font: inherit;
  line-height: 1.55;
  background: rgba(2, 35, 91, 0.66);
  box-shadow: inset 0 0 0.55vw rgba(35, 168, 255, 0.08);
  resize: vertical;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.application-page .review-comment-input .el-textarea__inner::placeholder {
  color: rgba(181, 222, 240, 0.55);
}

.application-page .review-comment-input .el-textarea__inner:focus {
  border-color: rgba(122, 232, 255, 0.82);
  box-shadow: 0 0 0 0.16vw rgba(64, 191, 255, 0.16), inset 0 0 0.55vw rgba(35, 168, 255, 0.1);
}
</style>

<style scoped>
.application-meta {
  top: 4.4vh;
  left: 1.4vw;
  font-size: clamp(13px, 0.9vw, 18px);
}

.application-main {
  padding-top: 0.8vh;
  scrollbar-width: thin;
  scrollbar-color: rgba(92, 204, 255, 0.38) transparent;
}

.application-main::-webkit-scrollbar,
.application-page .table-wrap::-webkit-scrollbar,
.detail-content::-webkit-scrollbar {
  width: 7px;
  height: 7px;
}

.application-main::-webkit-scrollbar-track,
.application-page .table-wrap::-webkit-scrollbar-track,
.detail-content::-webkit-scrollbar-track {
  background: transparent;
}

.application-main::-webkit-scrollbar-thumb,
.application-page .table-wrap::-webkit-scrollbar-thumb,
.detail-content::-webkit-scrollbar-thumb {
  border: 2px solid transparent;
  border-radius: 8px;
  background: rgba(92, 204, 255, 0.38);
  background-clip: padding-box;
}

.application-main::-webkit-scrollbar-thumb:hover {
  background-color: rgba(92, 204, 255, 0.62);
}

.application-main::-webkit-scrollbar-button {
  display: none;
}

.application-page-actions {
  position: sticky;
  top: 0;
  z-index: 10;
  min-height: 3.2vw;
  margin: 0 0 1.1vh 0.7vw;
  padding: 8px 0;
  gap: 0.65vw;
  background: rgba(4, 32, 76, 0.96);
  border-bottom: 1px solid rgba(82, 197, 255, 0.2);
}

.application-tab,
.application-page-actions .header-action {
  height: 2.45vw;
  min-height: 40px;
  padding: 0 1.25vw;
  border-radius: 0.4vw;
  font-size: clamp(14px, 0.9vw, 19px);
}

.application-tab-badge {
  min-width: 21px;
  height: 21px;
  margin-left: 7px;
  padding: 0 5px;
  border-radius: 11px;
  font-size: 13px;
  line-height: 21px;
}

.modal-backdrop {
  padding: 14px;
  overflow-x: hidden;
  overflow-y: auto;
  align-items: flex-start;
}

.detail-modal {
  width: min(1220px, 100%);
  max-height: calc(100vh - 28px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.detail-header {
  flex: 0 0 auto;
  min-height: 66px;
  padding: 10px 22px;
}

.detail-content {
  flex: 1 1 auto;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 13px 22px 10px;
}

.detail-grid {
  gap: 8px 18px;
}

.detail-grid span {
  margin-bottom: 3px;
}

.detail-grid strong {
  font-size: 12px;
}

.flow-section {
  margin-top: 12px;
}

.detail-section-heading {
  margin-bottom: 8px;
}

.flow-scroll {
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: 2px;
}

.flow-card {
  width: 230px;
  min-height: 238px;
  padding: 10px 10px 9px;
}

.flow-index {
  width: 27px;
  height: 27px;
  margin-bottom: 6px;
  line-height: 27px;
  font-size: 12px;
}

.flow-card h4 {
  margin-bottom: 5px;
  font-size: 13px;
}

.flow-role,
.flow-key,
.flow-operator,
.flow-detail {
  font-size: 11px;
}

.flow-key {
  margin-top: 2px;
}

.flow-users {
  min-height: 48px;
  margin: 7px 0 7px;
  padding: 5px 6px;
}

.flow-users > span,
.flow-users > em,
.flow-user-list i {
  font-size: 10px;
}

.flow-user-list {
  max-height: 30px;
  margin-top: 3px;
  overflow-y: auto;
}

.flow-user-list i {
  display: block;
  overflow: hidden;
  padding: 2px 5px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.flow-status {
  padding: 3px 7px;
  font-size: 10px;
}

.flow-operator {
  margin-top: 6px;
}

.flow-detail {
  margin-top: 3px;
}

.flow-arrow {
  width: 32px;
  font-size: 20px;
}

.next-node {
  margin-top: 10px;
  padding: 8px 12px;
}

.review-section {
  margin-top: 10px;
  padding-top: 9px;
}

.review-section .form-field {
  margin-bottom: 0;
}

.review-section .form-field > span {
  margin-bottom: 4px;
}

.review-section .form-field textarea {
  height: 52px;
  min-height: 52px;
  resize: none;
}

.detail-footer {
  flex: 0 0 auto;
  min-height: 50px;
  padding: 10px 22px;
}

.detail-footer > button {
  width: 88px;
  min-height: 38px;
  flex: 0 0 88px;
  padding: 0;
}

@media (max-width: 720px) {
  .application-page-actions {
    min-height: 0;
    margin: 0 0 1.1vh;
    gap: 5px;
  }

  .application-tab,
  .application-page-actions .header-action {
    min-height: 36px;
    height: 36px;
    padding: 0 12px;
    border-radius: 4px;
    font-size: 13px;
  }

  .detail-modal {
    height: calc(100vh - 20px);
  }

  .detail-content {
    padding: 10px 13px 8px;
  }

  .detail-header {
    padding: 9px 14px;
  }

  .flow-card {
    width: 205px;
  }

  .detail-footer {
    padding: 8px 14px;
  }

  .detail-footer > button {
    width: 76px;
    flex-basis: 76px;
  }
}
</style>
